import React from 'react';
import { Info, CheckCircle, AlertTriangle, BookOpen, Users, Building, Calendar, FileText } from 'lucide-react';

/**
 * Информационный компонент с полезной информацией о расписаниях
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о составлении расписания: принципы, требования, особенности
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-500" />
          Что такое расписание и для чего оно нужно?
        </h3>
        <p>
          <strong>Расписание занятий</strong> — это документ, который определяет временную и пространственную организацию учебного процесса. Грамотно составленное расписание обеспечивает эффективную реализацию учебных планов, оптимальное использование учебных помещений, технических средств и времени как учащихся, так и преподавателей.
        </p>
        <p>
          Хорошее расписание является основой слаженной работы образовательного учреждения и напрямую влияет на качество образовательного процесса, уровень утомляемости учащихся и преподавателей, а также на эффективность использования ресурсов учебного заведения.
        </p>

        <h3 className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-500" />
          Основные принципы составления расписания
        </h3>
        <ul>
          <li><strong>Педагогические принципы:</strong> учет закономерностей изменения работоспособности учащихся в течение дня и недели, рациональное чередование различных по сложности предметов.</li>
          <li><strong>Организационные принципы:</strong> обеспечение выполнения учебных планов и программ, рациональное использование материально-технической базы.</li>
          <li><strong>Психологические принципы:</strong> создание комфортных условий для всех участников образовательного процесса, минимизация стрессовых ситуаций.</li>
          <li><strong>Санитарно-гигиенические принципы:</strong> соблюдение требований к режиму образовательного процесса, предотвращение переутомления.</li>
        </ul>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Российская специфика составления школьного расписания
          </h4>
          <p className="text-green-600 dark:text-green-400 text-sm mb-0">
            В России при составлении расписания для школ используется шкала трудности учебных предметов И.Г. Сивкова. 
            Согласно этой шкале, для начальных классов наиболее сложными считаются математика (8 баллов), русский и иностранный языки (7 баллов), 
            для 5-11 классов - математика, химия, физика и иностранный язык. Рекомендуется планировать эти предметы на 2-4 уроки в дни с наиболее
            высокой работоспособностью (вторник, среда).
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Нормативные требования в России
        </h3>
        <p>
          В Российской Федерации составление расписания регламентируется следующими нормативными документами:
        </p>
        <ul>
          <li><strong>СанПиН 2.4.2.2821-10</strong> "Санитарно-эпидемиологические требования к условиям и организации обучения в общеобразовательных учреждениях"</li>
          <li><strong>СанПиН 2.4.3648-20</strong> "Санитарно-эпидемиологические требования к организациям воспитания и обучения, отдыха и оздоровления детей и молодежи"</li>
          <li><strong>ФГОС</strong> всех уровней образования</li>
          <li><strong>Приказы и распоряжения Министерства просвещения РФ</strong></li>
        </ul>
        
        <p>
          Согласно СанПиН, должны соблюдаться следующие основные требования:
        </p>
        <ul>
          <li>Учебные занятия начинаются не ранее 8:00 (рекомендуемое время начала занятий — 9:00)</li>
          <li>Недельная образовательная нагрузка распределяется на все рабочие дни недели, при этом объем максимальной нагрузки в течение дня не должен превышать установленных норм для разных классов</li>
          <li>Перерывы между уроками — не менее 10 минут, большая перемена (после 2-го или 3-го уроков) — 20-30 минут</li>
          <li>Для питания предусматривается перерыв достаточной продолжительности</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Building className="w-5 h-5 text-orange-500" />
          Особенности составления расписания для разных типов учебных заведений
        </h3>
        
        <h4>Школы</h4>
        <ul>
          <li>Учет возрастных особенностей учащихся</li>
          <li>Соблюдение гигиенической кривой умственной работоспособности в течение дня и недели</li>
          <li>Чередование предметов разной сложности</li>
          <li>В начальной школе не рекомендуется проводить сдвоенные уроки</li>
          <li>Предметы, требующие больших затрат времени на домашнюю подготовку, не следует ставить в один день</li>
        </ul>
        
        <h4>ВУЗы и колледжи</h4>
        <ul>
          <li>Учет специфики направления подготовки</li>
          <li>Минимизация "окон" между парами</li>
          <li>Равномерное распределение аудиторной нагрузки по дням недели</li>
          <li>Учет особенностей работы преподавателей-совместителей</li>
          <li>Разделение потоков на лекционные и семинарские занятия</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Типичные ошибки при составлении расписания
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Игнорирование уровня сложности предметов при их распределении в течение дня и недели</li>
            <li>Несоблюдение требований СанПиН по максимально допустимой нагрузке</li>
            <li>Необоснованная концентрация сложных предметов в один день</li>
            <li>Нерациональное использование учебных кабинетов</li>
            <li>Слишком большие перерывы ("окна") в расписании преподавателей или групп учащихся</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-500" />
          Учет интересов участников образовательного процесса
        </h3>
        
        <h4>Учащиеся</h4>
        <p>
          Для учащихся важно создать расписание, которое обеспечивает оптимальную умственную работоспособность, предотвращает переутомление, 
          учитывает периоды активности в течение дня, а также специфику разных предметов и их сложность.
        </p>
        
        <h4>Преподаватели</h4>
        <p>
          Для преподавателей важны равномерное распределение нагрузки в течение недели, минимизация "окон" между занятиями, 
          учет методических дней и возможности для повышения квалификации.
        </p>
        
        <h4>Администрация</h4>
        <p>
          Для администрации образовательного учреждения важно обеспечить выполнение учебных планов и программ, 
          эффективное использование материально-технической базы, соблюдение нормативных требований.
        </p>

        <h3 className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-500" />
          Современные подходы к составлению расписания
        </h3>
        <p>
          Современные методы составления расписания предполагают использование специализированного программного обеспечения, 
          которое позволяет автоматизировать процесс и учесть множество параметров и ограничений. Такой подход дает следующие преимущества:
        </p>
        <ul>
          <li>Значительная экономия времени сотрудников, ответственных за составление расписания</li>
          <li>Возможность быстрого внесения изменений и корректировок</li>
          <li>Минимизация человеческого фактора и связанных с ним ошибок</li>
          <li>Возможность оперативного информирования всех участников образовательного процесса об изменениях в расписании</li>
          <li>Оптимизация использования учебных помещений и оборудования</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш генератор расписаний поможет вам создать оптимальное расписание, учитывающее все необходимые требования и особенности вашего учебного заведения!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;